import { USER_ROLES } from "@/utils/permissions";
import { isReplayComplete } from "@/utils/replay";
import { getSessionDateKey } from "@/utils/sessionDateTime";

export const SESSION_VIEW_PRESETS = Object.freeze({
  ADMIN_ALL: "admin_all",
  TEACHER_MY: "teacher_my",
  TEACHER_PUBLIC: "teacher_public",
  TEACHER_SHARED: "teacher_shared",
});

const SESSION_COLUMN_LABELS = Object.freeze({
  gameId: "session-column-game-id",
  hostUsername: "session-column-host-username",
  hostEmail: "session-column-host-email",
  joinCode: "session-column-join-code",
  discipline: "session-column-discipline",
  startTime: "session-column-start-time",
  endTime: "session-column-end-time",
  duration: "session-column-duration",
  action: "session-column-action",
});

const DEFAULT_SESSION_COLUMN_KEYS = Object.freeze([
  "gameId",
  "hostUsername",
  "hostEmail",
  "joinCode",
  "discipline",
  "startTime",
  "endTime",
  "duration",
  "action",
]);

const TEACHER_OWN_SESSION_COLUMN_KEYS = Object.freeze(
  DEFAULT_SESSION_COLUMN_KEYS.filter((key) => key !== "hostUsername" && key !== "hostEmail")
);

const DEFAULT_SESSION_FILTER_FIELDS = Object.freeze([
  "host",
  "joinCode",
  "gameId",
  "discipline",
  "date",
]);

const TEACHER_OWN_SESSION_FILTER_FIELDS = Object.freeze(
  DEFAULT_SESSION_FILTER_FIELDS.filter((field) => field !== "host")
);

const SESSION_VIEW_CONFIG = Object.freeze({
  [SESSION_VIEW_PRESETS.ADMIN_ALL]: {
    columns: DEFAULT_SESSION_COLUMN_KEYS,
    filters: DEFAULT_SESSION_FILTER_FIELDS,
    detail: {
      showHostOverview: true,
      allowDownloads: true,
      allowReport: true,
      allowVisibilitySwitch: true,
    },
  },
  [SESSION_VIEW_PRESETS.TEACHER_MY]: {
    columns: TEACHER_OWN_SESSION_COLUMN_KEYS,
    filters: TEACHER_OWN_SESSION_FILTER_FIELDS,
    detail: {
      showHostOverview: false,
      allowDownloads: true,
      allowReport: true,
      allowVisibilitySwitch: true,
    },
  },
  [SESSION_VIEW_PRESETS.TEACHER_PUBLIC]: {
    columns: DEFAULT_SESSION_COLUMN_KEYS,
    filters: DEFAULT_SESSION_FILTER_FIELDS,
    detail: {
      showHostOverview: true,
      allowDownloads: true,
      allowReport: true,
      allowVisibilitySwitch: false,
    },
  },
  [SESSION_VIEW_PRESETS.TEACHER_SHARED]: {
    columns: DEFAULT_SESSION_COLUMN_KEYS,
    filters: DEFAULT_SESSION_FILTER_FIELDS,
    detail: {
      showHostOverview: true,
      allowDownloads: true,
      allowReport: true,
      allowVisibilitySwitch: false,
    },
  },
});

function getSessionViewConfig(viewPreset) {
  return SESSION_VIEW_CONFIG[viewPreset] || SESSION_VIEW_CONFIG[SESSION_VIEW_PRESETS.ADMIN_ALL];
}

function buildSessionColumns(columnKeys = []) {
  return columnKeys.map((key) => ({
    key,
    label: SESSION_COLUMN_LABELS[key] || key,
  }));
}

export function createSessionFilterState({ includeTeacherIds = false } = {}) {
  return {
    host: "",
    hostUserId: null,
    joinCode: "",
    gameId: "",
    discipline: "",
    dateMode: "single",
    singleDate: "",
    dateFrom: "",
    dateTo: "",
    ...(includeTeacherIds ? { teacherIds: [] } : {}),
  };
}

export function getSessionStartDateKey(startTime, timeZone = "UTC") {
  return getSessionDateKey(startTime, timeZone);
}

export function resolveSessionViewPreset({
  viewerRole = USER_ROLES.ADMIN,
  publicGamesOnly = false,
  sharedWithMeOnly = false,
} = {}) {
  if (viewerRole === USER_ROLES.RESEARCHER) {
    if (sharedWithMeOnly) return SESSION_VIEW_PRESETS.TEACHER_SHARED;
    return SESSION_VIEW_PRESETS.TEACHER_PUBLIC;
  }

  if (viewerRole === USER_ROLES.TEACHER) {
    if (sharedWithMeOnly) return SESSION_VIEW_PRESETS.TEACHER_SHARED;
    if (publicGamesOnly) return SESSION_VIEW_PRESETS.TEACHER_PUBLIC;
    return SESSION_VIEW_PRESETS.TEACHER_MY;
  }

  return SESSION_VIEW_PRESETS.ADMIN_ALL;
}

export function getSessionColumns(viewPreset) {
  return buildSessionColumns(getSessionViewConfig(viewPreset).columns);
}

export function getSessionFilterFields(viewPreset) {
  return getSessionViewConfig(viewPreset).filters;
}

export function shouldShowSessionReport(gameDetails) {
  if (!gameDetails) return false;
  const phase = gameDetails.currentPhase;
  return phase === "DOWNLOAD" || phase === "END" || phase === "Download" || phase === "End";
}

export function getSessionDetailCapabilities({ gameDetails, accessScope } = {}) {
  const detailConfig = getSessionViewConfig(accessScope).detail || {};

  return {
    showHostOverview: detailConfig.showHostOverview !== false,
    canDownloadFiles: detailConfig.allowDownloads !== false,
    canGenerateReport: detailConfig.allowReport !== false && shouldShowSessionReport(gameDetails),
    canSwitchVisibility: detailConfig.allowVisibilitySwitch !== false && Boolean(gameDetails?.canToggleVisibility),
    canShareGame: Boolean(gameDetails?.canShareGame),
    canReplay: isReplayComplete(gameDetails),
  };
}

export function getSessionDetailTabs({ gameDetails, accessScope } = {}) {
  const capabilities = getSessionDetailCapabilities({ gameDetails, accessScope });
  return [
    { id: "overview", label: "Overview & Config", visible: true },
    { id: "replay", label: "Replay", visible: capabilities.canReplay },
    { id: "players", label: "Players & Actions", visible: true },
    { id: "messages", label: "Messages & Downloads", visible: capabilities.canDownloadFiles },
    { id: "report", label: "AI Report", visible: capabilities.canGenerateReport },
    { id: "share", label: "Share", visible: capabilities.canShareGame },
  ];
}

export function getOverviewFields(gameDetails, { accessScope } = {}) {
  if (!gameDetails) return [];
  const capabilities = getSessionDetailCapabilities({ gameDetails, accessScope });

  return [
    { key: "gameId", label: "Game ID", value: gameDetails.gameId },
    { key: "joinCode", label: "Join Code", value: gameDetails.joinCode || "N/A" },
    capabilities.showHostOverview
      ? { key: "hostUsername", label: "Host", value: gameDetails.hostUsername || "N/A" }
      : null,
    { key: "startTime", label: "Start Time", value: gameDetails.startTime || "N/A" },
    { key: "endTime", label: "End Time", value: gameDetails.endTime || "N/A" },
    { key: "duration", label: "Duration", value: gameDetails.duration || "N/A" },
    { key: "currentPhase", label: "Current Phase", value: gameDetails.currentPhase || "N/A" },
  ].filter(Boolean);
}

export function getConfigFields(gameDetails) {
  if (!gameDetails) return [];

  return [
    gameDetails.shareGradeLevel
      ? { key: "shareGradeLevel", label: "Grade Level", value: gameDetails.shareGradeLevel }
      : null,
    gameDetails.shareSubjectDiscipline
      ? { key: "shareSubjectDiscipline", label: "Shared Subject", value: gameDetails.shareSubjectDiscipline }
      : null,
    gameDetails.shareLearningStandard
      ? { key: "shareLearningStandard", label: "Learning Standard", value: gameDetails.shareLearningStandard }
      : null,
    { key: "playerNum", label: "Number of Players", value: gameDetails.playerNum || "N/A" },
    { key: "groupLimit", label: "Max Players per Group", value: gameDetails.groupLimit || "N/A" },
    { key: "readingTime", label: "Reading Time (minutes)", value: gameDetails.readingTime || "N/A" },
    { key: "discussionTime", label: "Discussion Time (minutes)", value: gameDetails.discussionTime || "N/A" },
    { key: "discipline", label: "Discipline", value: gameDetails.discipline || "N/A" },
    { key: "language", label: "Language", value: gameDetails.language || "N/A" },
    { key: "llm", label: "LLM Model", value: gameDetails.llm || "N/A" },
    { key: "enableVoteReason", label: "Vote Reason Required", value: gameDetails.enableVoteReason ? "Yes" : "No" },
    {
      key: "enablePreliminaryVoteReason",
      label: "Preliminary Vote Reason",
      value: gameDetails.enablePreliminaryVoteReason ? "Yes" : "No",
    },
    gameDetails.isPublic != null
      ? {
          key: "visibility",
          label: "Visibility",
          value: gameDetails.isPublic === 1 ? "Public" : "Private",
        }
      : null,
  ].filter(Boolean);
}

export function getTextSummaryFields(gameDetails) {
  if (!gameDetails || !gameDetails.texts || gameDetails.texts.length === 0) return [];

  return [
    gameDetails.textTitle || (gameDetails.texts[0] && gameDetails.texts[0].title)
      ? { key: "textTitle", label: "Title", value: gameDetails.textTitle || gameDetails.texts[0].title }
      : null,
    gameDetails.scientificArguments
      ? { key: "scientificArguments", label: "Arguments (scientific)", value: gameDetails.scientificArguments }
      : null,
    { key: "textCount", label: "Number of Texts", value: gameDetails.texts.length },
    gameDetails.realTextId
      ? { key: "realTextId", label: "Real Text ID", value: gameDetails.realTextId }
      : null,
  ].filter(Boolean);
}
